import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Box, Typography, TextField } from '@mui/material';
import dayjs from 'dayjs';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import CustomMultiSelectDropDown from 'packages/novous_web/src/common/components/CustomMultiSelectDropDownInput';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import refresh from 'packages/novous_web/src/assets/icons/Refresh.svg';
import arrowUp from 'packages/novous_web/src/assets/icons/ArrowBlack.svg';
import arrowDown from 'packages/novous_web/src/assets/icons/ArrowDownBlack.svg';
import DateField from 'packages/novous_web/src/common/components/DateField';

interface DashboardAnalyticsFilterDataProps {
  open: boolean;
  onClose: () => void;
  vehicleOptions: { label: string; value: string }[];
  groupOptions: { label: string; value: string }[];
  onApply: (filters: {
    vehicleIds: string[];
    vehicleGroup: string;
    dateRange: string;
    startDate?: string;
    endDate?: string;
    view: string;
  }) => void;
  selectedView: string;
  onReset?: () => void;
}

const DashboardAnalyticsFilterData: React.FC<DashboardAnalyticsFilterDataProps> = ({
  open,
  onClose,
  vehicleOptions,
  groupOptions,
  onApply,
  selectedView,
  onReset,
}) => {
  const [selectedVehicle, setSelectedVehicle] = useState<(string | number)[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | number>('');
  const [dateRange, setDateRange] = useState<string>('7 Days');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [clear, setClear] = useState(false);

  // Track initial values when modal opens
  const [initialVehicle, setInitialVehicle] = useState<(string | number)[]>([]);
  const [initialGroup, setInitialGroup] = useState<string | number>('');
  const [initialDateRange, setInitialDateRange] = useState<string>('7 Days');
  const [initialStartDate, setInitialStartDate] = useState<string>('');
  const [initialEndDate, setInitialEndDate] = useState<string>('');

  // Collapsible sections
  const [vehicleExpanded, setVehicleExpanded] = useState(true);
  const [groupExpanded, setGroupExpanded] = useState(true);
  const [dateExpanded, setDateExpanded] = useState(true);

  // Store initial values when modal opens
  useEffect(() => {
    if (open) {
      setInitialVehicle([...selectedVehicle]);
      setInitialGroup(selectedGroup);
      setInitialDateRange(dateRange);
      setInitialStartDate(customStartDate);
      setInitialEndDate(customEndDate);
    }
  }, [open]);

  // Reset filters when view changes
  useEffect(() => {
    resetFilters();
  }, [selectedView]);

  const resetFilters = () => {
    setSelectedVehicle([]);
    setSelectedGroup('');
    setDateRange('7 Days');
    setCustomStartDate('');
    setCustomEndDate('');
  };

  useEffect(() => {
    if (clear) {
      resetFilters();
      setClear(false);
      onReset?.();
    }
  }, [clear]);

  const handleStartDateChange = (value: string | null) => {
    setCustomStartDate(value || '');
  };

  const handleEndDateChange = (value: string | null) => {
    setCustomEndDate(value || '');
  };

  const convertToYYYYMMDD = (dateString: string): string => {
    if (!dateString) return '';

    const parts = dateString.split('/');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateString; // Return as is if format doesn't match
  };

  // Customize filter options based on selected view
  let vehicleOptionsForView = vehicleOptions;
  let groupOptionsForView = groupOptions;

  const dateRangesForView = useMemo(() => {
    const computeCustomLabel = () => {
      if (!customStartDate || !customEndDate) return 'Custom';

      const start = dayjs(convertToYYYYMMDD(customStartDate));
      const end = dayjs(convertToYYYYMMDD(customEndDate));

      if (!start.isValid() || !end.isValid() || end.isBefore(start)) return 'Custom';

      const diff = end.diff(start, 'day') + 1; // inclusive of both start and end
      return `${diff} Days`;
    };

    const customLabel = computeCustomLabel();

    return [
      { label: '7 Days', value: '7 Days' },
      { label: '15 Days', value: '15 Days' },
      { label: '30 Days', value: '30 Days' },
      { label: customLabel, value: 'Custom' },
    ];
  }, [customStartDate, customEndDate]);

  const handleApply = () => {
    let startDate: string | undefined;
    let endDate: string | undefined = dayjs().format('YYYY-MM-DD');

    if (dateRange === '7 Days') startDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
    else if (dateRange === '15 Days') startDate = dayjs().subtract(15, 'day').format('YYYY-MM-DD');
    else if (dateRange === '30 Days') startDate = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
    else if (dateRange === 'Custom') {
      startDate = customStartDate ? convertToYYYYMMDD(customStartDate) : undefined;
      endDate = customEndDate ? convertToYYYYMMDD(customEndDate) : undefined;
    }

    const appliedFilters = {
      vehicleIds: selectedVehicle.map(String),
      vehicleGroup: String(selectedGroup),
      dateRange,
      startDate,
      endDate,
      view: selectedView,
    };

    console.log('🔍 FILTER MODAL - Sending filters:', appliedFilters);
    console.log('🔍 FILTER MODAL - Selected Vehicles:', selectedVehicle);
    console.log('🔍 FILTER MODAL - Selected Vehicles Length:', selectedVehicle.length);

    if (selectedVehicle.length === 0) {
      console.log('✅ FILTER CLEARED - No vehicles selected, will use fleetId');
    }

    onApply(appliedFilters);
    onClose();
  };

  const hasChanges = useMemo(() => {
    const vehicleChanged = JSON.stringify(selectedVehicle.sort()) !== JSON.stringify(initialVehicle.sort());
    const groupChanged = selectedGroup !== initialGroup;
    const dateRangeChanged = dateRange !== initialDateRange;
    const startDateChanged = customStartDate !== initialStartDate;
    const endDateChanged = customEndDate !== initialEndDate;

    return vehicleChanged || groupChanged || dateRangeChanged || startDateChanged || endDateChanged;
  }, [
    selectedVehicle,
    selectedGroup,
    dateRange,
    customStartDate,
    customEndDate,
    initialVehicle,
    initialGroup,
    initialDateRange,
    initialStartDate,
    initialEndDate,
  ]);

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="filter-modal-title">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '70%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          overflowY: 'auto',
          height: '80vh',
        }}
      >
        <div className="flex flex-col gap-4 h-full relative">
          {/* Header */}
          <div className="flex justify-between items-center">
            <Typography variant="h6">Filter Options</Typography>
            <CustomIconButton
              text="Clear All"
              icon={<img src={refresh} alt="reset" />}
              background="transparent"
              variant="outlined"
              onClick={() => setClear(true)}
            />
          </div>

          {/* Vehicle Section */}
          <div className="flex flex-col gap-2">
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => setVehicleExpanded((prev) => !prev)}
            >
              <Typography variant="body1">Vehicle</Typography>
              <img src={vehicleExpanded ? arrowUp : arrowDown} width={24} height={24} alt="toggle" />
            </div>
            {vehicleExpanded && (
              <CustomMultiSelectDropDown
                options={vehicleOptionsForView}
                value={selectedVehicle}
                onChange={setSelectedVehicle}
                placeholder="Select Vehicle"
                showDropdownArrow
              />
            )}
          </div>

          {/* Group Section */}
          <div className="flex flex-col gap-2">
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => setGroupExpanded((prev) => !prev)}
            >
              <Typography variant="body1">Depot</Typography>
              <img src={groupExpanded ? arrowUp : arrowDown} width={24} height={24} alt="toggle" />
            </div>
            {groupExpanded && (
              <CustomDropDownInput
                options={groupOptionsForView}
                value={selectedGroup}
                onChange={setSelectedGroup}
                placeholder="Select Depot"
                showDropdownArrow
              />
            )}
          </div>

          {/* Date Range Section */}
          <div className="flex flex-col gap-2">
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => setDateExpanded((prev) => !prev)}
            >
              <Typography variant="body1">Date Range</Typography>
              <img src={dateExpanded ? arrowUp : arrowDown} width={24} height={24} alt="toggle" />
            </div>
            {dateExpanded && (
              <>
                <CustomDropDownInput
                  options={dateRangesForView}
                  value={dateRange}
                  onChange={setDateRange}
                  placeholder="Select Date Range"
                  showDropdownArrow
                />
                {dateRange === 'Custom' && (
                  <div className="flex gap-2 mt-2">
                    <DateField
                      label="Start Date"
                      name="startDate"
                      value={customStartDate}
                      onChange={handleStartDateChange}
                      maxDate={customEndDate || dayjs().format('DD/MM/YYYY')}
                    />
                    <DateField
                      label="End Date"
                      name="endDate"
                      value={customEndDate}
                      onChange={handleEndDateChange}
                      minDate={customStartDate}
                      maxDate={dayjs().format('DD/MM/YYYY')}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4 justify-between absolute bottom-0 left-0 right-0">
            <CustomIconButton text="Cancel" variant="outlined" background="transparent" onClick={onClose} />
            <CustomIconButton
              text="Apply"
              variant="contained"
              onClick={handleApply}
              isDisabled={!hasChanges}
            />
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default DashboardAnalyticsFilterData;
